import moment from "moment";
import {
  mockSubuserInvoicesData,
  mockTaxRatesData,
  mockTenantInvoiceConfigData,
} from "../data/mockData/wlDemoData";
import { paginateRows } from "./listQueryUtils";
import { applySubUserSeedToRows } from "../data/mockData/subUserSeedAssignments";
import { resolveSubuserStatementsList } from "./subuserStatementDemoHelpers";
import {
  getDemoSubuserInvoiceRows,
  getSubuserInvoiceOverridesMap,
  getTaxRateDeletedIds,
  getTaxRateExtras,
  getTenantInvoiceConfigOverride,
  markStatementsInvoiced,
  markTaxRateDeleted,
  setTenantInvoiceConfigOverride,
  upsertDemoSubuserInvoice,
  upsertSubuserInvoiceOverride,
  upsertTaxRateExtra,
} from "./demoInvoiceSessionStore";

function mergeById(baseRows, extraRows) {
  const map = new Map();
  (baseRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), row);
    }
  });
  (extraRows || []).forEach(row => {
    if (row && row.id != null) {
      map.set(String(row.id), { ...(map.get(String(row.id)) || {}), ...row });
    }
  });
  return Array.from(map.values());
}

function applySubuserInvoiceOverrides(rows) {
  const overrides = getSubuserInvoiceOverridesMap();
  return (rows || []).map(row => {
    const patch = overrides[String(row.id)];
    return patch ? { ...row, ...patch } : row;
  });
}

export function getSubuserInvoiceRows() {
  const merged = mergeById(mockSubuserInvoicesData, getDemoSubuserInvoiceRows());
  return applySubuserInvoiceOverrides(merged);
}

export function resolveSubuserInvoicesList(queryParams, subUserIdFromPath = null) {
  const rows = applySubUserSeedToRows("subuser-invoices", getSubuserInvoiceRows());
  return paginateRows(rows, queryParams, { subUserIdFromPath });
}

function getNextSubuserInvoiceId(rows) {
  return (rows || []).reduce((max, row) => {
    const val = Number(row?.id) || 0;
    return val > max ? val : max;
  }, 400) + 1;
}

export function handleGenerateDemoSubuserInvoice() {
  const list = resolveSubuserStatementsList(
    new URLSearchParams({
      status: "0",
      invoice_generated: "false",
      page_size: "9999",
    }),
  );
  const openStatements = list?.results || [];

  if (!openStatements.length) {
    throw new Error("No statement available for invoicing.");
  }

  const rows = getSubuserInvoiceRows();
  const nextId = getNextSubuserInvoiceId(rows);
  const gross = openStatements.reduce(
    (sum, statement) => sum + (Number(statement.price) || 0),
    0,
  );
  const tax = Math.round(gross * 0.2 * 100) / 100;
  const now = moment();

  const invoice = {
    id: nextId,
    invoice_number: `SUB-${now.format("DDMMYY")}${String(nextId).padStart(3, "0")}`,
    name: "Subuser Invoice",
    owner: "Demo Subuser",
    issue_date: now.format("DD.MM.YYYY"),
    gross_amount: gross.toFixed(2),
    tax_amount: tax.toFixed(2),
    net_amount: (gross + tax).toFixed(2),
    currency: 0,
    status: 0,
    invoice_pdf: "",
  };

  upsertDemoSubuserInvoice(invoice);
  markStatementsInvoiced(openStatements.map(statement => statement.id));

  return invoice;
}

export function handleUpdateSubuserInvoiceStatus(payload) {
  const invoiceId = payload?.invoice_id ?? payload?.id;
  const status = payload?.status;

  if (invoiceId == null || invoiceId === "") {
    throw new Error("Invoice id is required.");
  }
  if (status == null || status === "") {
    throw new Error("Status is required.");
  }

  const row = getSubuserInvoiceRows().find(
    invoice => String(invoice.id) === String(invoiceId),
  );
  if (!row) {
    throw new Error("Invoice not found.");
  }

  upsertSubuserInvoiceOverride(invoiceId, { status: Number(status) });
  return { detail: "Invoice status updated." };
}

export function getTenantInvoiceConfig() {
  const override = getTenantInvoiceConfigOverride();
  return { ...mockTenantInvoiceConfigData, ...(override || {}) };
}

export function handleUpdateTenantInvoiceConfig(payload) {
  const current = getTenantInvoiceConfig();
  const next = {
    ...current,
    ...(payload && typeof payload === "object" ? payload : {}),
  };
  setTenantInvoiceConfigOverride(next);
  return next;
}

export function getTaxRateRows() {
  const deleted = new Set(getTaxRateDeletedIds());
  const merged = mergeById(mockTaxRatesData, getTaxRateExtras());
  return merged.filter(row => !deleted.has(String(row.id)));
}

export function resolveTaxRatesList(queryParams) {
  const rows = getTaxRateRows();
  return paginateRows(rows, queryParams);
}

function getNextTaxRateId(rows) {
  return (rows || []).reduce((max, row) => {
    const val = Number(row?.id) || 0;
    return val > max ? val : max;
  }, 700) + 1;
}

function normalizeTaxRatePayload(payload, id) {
  const rate =
    payload?.rate === 0 || payload?.rate
      ? String(Number(payload.rate).toFixed(2))
      : "0.00";

  return {
    id,
    name: String(payload?.name || "").trim(),
    rate,
    category: payload?.category || "vat_gst",
    calculation_method: payload?.calculation_method || "exclusive",
    legal_text: payload?.legal_text || "",
  };
}

export function handleCreateTaxRate(payload) {
  if (!String(payload?.name || "").trim()) {
    throw new Error("Tax name is required.");
  }

  const rows = getTaxRateRows();
  const nextId = getNextTaxRateId(rows);
  const row = normalizeTaxRatePayload(payload, nextId);
  upsertTaxRateExtra(row);
  return row;
}

export function handleUpdateTaxRate(id, payload) {
  const rows = getTaxRateRows();
  const existing = rows.find(row => String(row.id) === String(id));
  if (!existing) {
    throw new Error("Tax rate not found.");
  }

  const row = normalizeTaxRatePayload({ ...existing, ...payload }, existing.id);
  upsertTaxRateExtra(row);
  return row;
}

export function handleDeleteTaxRate(id) {
  const rows = getTaxRateRows();
  const existing = rows.find(row => String(row.id) === String(id));
  if (!existing) {
    throw new Error("Tax rate not found.");
  }
  markTaxRateDeleted(id);
  return { detail: "Tax rate deleted." };
}
